import { useEffect, useState, } from 'react';
import moment from 'moment';
import { Time, } from './constants';
/**
 * The 2 type of time series MODE platform support
 */
export var TimeSeriesType;
(function (TimeSeriesType) {
    TimeSeriesType["SIMPLE"] = "Simple";
    TimeSeriesType["COLLECTION"] = "Collection";
})(TimeSeriesType || (TimeSeriesType = {}));
/**
 * The 2 type of time series value we can request from TSDB, raw or aggregated values
 */
export var TimeSeriesValueType;
(function (TimeSeriesValueType) {
    TimeSeriesValueType["RAW"] = "Raw";
    TimeSeriesValueType["AGGREGATED"] = "Aggregated";
})(TimeSeriesValueType || (TimeSeriesValueType = {}));
/**
 * Define a set of time span type. NOTE: We will use the same name as 'moment' library uses so that we can easily do calculation on them.
 */
export var TimeSpan;
(function (TimeSpan) {
    TimeSpan["MINUTE"] = "minute";
    TimeSpan["HOUR"] = "hour";
    TimeSpan["DAY"] = "day";
    TimeSpan["WEEK"] = "week";
    TimeSpan["MONTH"] = "month";
    TimeSpan["YEAR"] = "year";
})(TimeSpan || (TimeSpan = {}));
/**
 * The millisecond values of each time span
 */
export const TimeSpanInMilliSeconds = Object.freeze({
    [TimeSpan.YEAR]: Time.YEAR_IN_MS,
    [TimeSpan.MONTH]: Time.MONTH_IN_MS,
    [TimeSpan.WEEK]: Time.WEEK_IN_MS,
    [TimeSpan.DAY]: Time.DAY_IN_MS,
    [TimeSpan.HOUR]: Time.HOUR_IN_MS,
    [TimeSpan.MINUTE]: Time.MINUTE_IN_MS,
});
/**
 * Get the time series data resolution e.g. 1 Month, 1 Day, 5 Hours, 15 Minutes, etc.... base on the timeSeriesData
 */
export const getTimeSeriesDataInterval = (timeSeriesData) => {
    const { resolution, data } = timeSeriesData;
    if (resolution) {
        if (resolution.includes('year')) {
            return {
                timeUnit: 'year',
                value: Number(resolution.substring(0, resolution.indexOf('year'))),
            };
        }
        if (resolution.includes('month')) {
            return {
                timeUnit: 'month',
                value: Number(resolution.substring(0, resolution.indexOf('month'))),
            };
        }
        if (resolution.includes('day')) {
            return {
                timeUnit: 'day',
                value: Number(resolution.substring(0, resolution.indexOf('day'))),
            };
        }
        if (resolution.includes('hour')) {
            return {
                timeUnit: 'hour',
                value: Number(resolution.substring(0, resolution.indexOf('hour'))),
            };
        }
        if (resolution.includes('min')) {
            return {
                timeUnit: 'minute',
                value: Number(resolution.substring(0, resolution.indexOf('min'))),
            };
        }
        if (resolution.includes('sec')) {
            return {
                timeUnit: 'second',
                value: Number(resolution.substring(0, resolution.indexOf('sec'))),
            };
        }
    }
    // try to get resolution from the data
    if (data) {
        const shortestInterval = data.reduce((min, [date], index) => {
            if (index > 0 && date && data[index - 1]) {
                // get the diff between this current data point's date and the previous point's data and see
                // if it is smaller than shortestInterval
                return Math.min(Date.parse(date) - Date.parse((data[index - 1][0])), min);
            }
            return min;
        }, Number.MAX_SAFE_INTEGER);
        if (shortestInterval !== Number.MAX_SAFE_INTEGER) {
            return {
                timeUnit: 'second',
                value: shortestInterval / 1000,
            };
        }
    }
    return {
        timeUnit: 'second',
        value: 1,
    };
};
/**
 * Merge an Map of time series metric data into 1 array of MultiSeriesDataPoint e.g.
 *
 * from
 *      {
 *          temperature: {data:[{'2020-03-10', 70}, {'2020-03-11', 68}, {'2020-03-12', 69}, {'2020-03-13', 74}, {'2020-03-14', 69}]}
 *          pressure: {data:[{'2020-03-10', 34}, {'2020-03-11', 22}, {'2020-03-13', 39}]}
 *          humidity: {data:[{'2020-03-10', 0.134}, {'2020-03-11', 0.43}, {'2020-03-12', 0.64}, {'2020-03-13', 0.23}]}
 *      }
 *
 * to
 *      [
 *          {date:'2020-03-10', values:{temperature:70, pressure:34, humidity:0.134}},
 *          {date:'2020-03-11', values:{temperature:68, pressure:22, humidity:0.43}},
 *          {date:'2020-03-12', values:{temperature:69, humidity:0.64}},
 *          {date:'2020-03-13', values:{temperature:74, pressure:39, humidity:0.23}},
 *          {date:'2020-03-14', values:{temperature:69}},
 *      ]
 *
 * NOTE: it is possible for data from different series not to have the same data at the same date e.g. on '2020-03-14', we don't have 'pressure'
 * or 'humidity' data.
 *
 * @param data
 */
export const mergeMetricDataToMultiMetricDataPoints = (data) => {
    /**
     * Build a list of all the possible data point dates. We will use a map instead of an Array for now so that we can
     * look up the object by date
     *
     * So 'dataMap' = {
     *         '2020-03-10': {date:'2020-03-10', values:{temperature:70, pressure:34, humidity:0.134}},
     *         '2020-03-11': {date:'2020-03-11', values:{temperature:68, pressure:22, humidity:0.43}},
     *         '2020-03-12': {date:'2020-03-12', values:{temperature:69, humidity:0.64}},
     *         '2020-03-13': {date:'2020-03-13', values:{temperature:74, pressure:39, humidity:0.23}},
     *         '2020-03-14': {date:'2020-03-14', values:{temperature:69}},
     *    }
     */
    const dataMap = Object.entries(data).reduce((result, [metricName, dataPoints]) => {
        // go through all the data points in this time series data and add the data points to the result
        dataPoints.forEach((dataPoint) => {
            if (!result[dataPoint.dateString]) {
                // this date does not exist in the result yet, add it to the result and initialize it with the data point's date
                // eslint-disable-next-line no-param-reassign
                result[dataPoint.dateString] = {
                    date: moment(dataPoint.dateString).toDate(),
                    dateValue: moment(dataPoint.dateString).valueOf(),
                    values: {
                        [metricName]: dataPoint.value,
                    },
                };
            }
            else {
                // There is already another a point exist for the current date, add this series value to the result
                // eslint-disable-next-line no-param-reassign
                result[dataPoint.dateString] = {
                    date: moment(dataPoint.dateString).toDate(),
                    dateValue: result[dataPoint.dateString].dateValue,
                    values: {
                        ...result[dataPoint.dateString].values,
                        [metricName]: dataPoint.value,
                    },
                };
            }
        });
        return result;
    }, {});
    // Convert the data map to data array and sort them by date
    const dataArray = Object.values(dataMap).sort((data1, data2) => {
        if (data1.dateValue > data2.dateValue) {
            return 1;
        }
        if (data1.dateValue < data2.dateValue) {
            return -1;
        }
        return 0;
    });
    return dataArray;
};
/**
 * Custom hook to merge a Map of time series metric data into 1 array of MultiSeriesDataPoint e.g.
 * This function does the same things as the mergeMetricDataToMultiMetricDataPoints function because it calls mergeMetricDataToMultiMetricDataPoints
 * to handle the merge logic. The only different is that this function is a HOOK so it can be used like a hook.
 *
 * from
 *      {
 *          temperature: {data:[{'2020-03-10', 70}, {'2020-03-11', 68}, {'2020-03-12', 69}, {'2020-03-13', 74}, {'2020-03-14', 69}]}
 *          pressure: {data:[{'2020-03-10', 34}, {'2020-03-11', 22}, {'2020-03-13', 39}]}
 *          humidity: {data:[{'2020-03-10', 0.134}, {'2020-03-11', 0.43}, {'2020-03-12', 0.64}, {'2020-03-13', 0.23}]}
 *      }
 *
 * to
 *      [
 *          {date:'2020-03-10', values:{temperature:70, pressure:34, humidity:0.134}},
 *          {date:'2020-03-11', values:{temperature:68, pressure:22, humidity:0.43}},
 *          {date:'2020-03-12', values:{temperature:69, humidity:0.64}},
 *          {date:'2020-03-13', values:{temperature:74, pressure:39, humidity:0.23}},
 *          {date:'2020-03-14', values:{temperature:69}},
 *      ]
 *
 * NOTE: it is possible for data from different series not to have the same data at the same date e.g. on '2020-03-14', we don't have 'pressure'
 * or 'humidity' data.
 *
 */
export const useMergeCachedMetricDataToMultiMetricDataPoints = (cachedMetricData) => {
    const [data, setData] = useState();
    useEffect(() => {
        setData({
            numericMetrics: mergeMetricDataToMultiMetricDataPoints(cachedMetricData.numericMetrics || {}),
            tagMetrics: mergeMetricDataToMultiMetricDataPoints(cachedMetricData.tagMetrics || {}),
        });
    }, [cachedMetricData]);
    return data;
};
/**
 * Custom hook to manage the list of series per chart. Given an array of metrics and a flag whether to combine them, return a DOUBLE array of metrics
 * E.g.
 *      Input: ["temperature", "pressure", "battery", "speed"]
 *
 *      Output: If combineSeries === true, there will only be 1 chart that display all metrics
 *              [
 *                  ["temperature", "pressure", "battery", "speed"]
 *              ]
 *
 *      Output: If combineSeries === false, we will display 1 metric per chart
 *              [
 *                  ["temperature"],
 *                  ["pressure"],
 *                  ["battery"],
 *                  ["speed"]
 *              ]
 */
export const useRebuildMetricsPerChartArray = (metrics, combineSeries, excludedMetricsNames) => {
    const [seriesPerChart, setSeriesPerChart] = useState([]);
    useEffect(() => {
        const filteredMetrics = excludedMetricsNames ? metrics.filter((metric) => {
            return !excludedMetricsNames.includes(metric.name);
        }) : metrics;
        if (filteredMetrics.length > 0) {
            if (combineSeries) {
                setSeriesPerChart([
                    [...filteredMetrics],
                ]);
            }
            else {
                setSeriesPerChart(filteredMetrics.map((metric) => {
                    return [metric];
                }));
            }
        }
        else {
            // no selected metrics so no series per chart
            setSeriesPerChart([]);
        }
    }, [metrics, combineSeries, excludedMetricsNames]);
    return seriesPerChart;
};
export const convertCollectionDataToMetricsDataPoints = (collectionData, valueNames, tagNames) => {
    // initialize the result data with empty arrays
    const resultDataInit = {
        numericMetrics: valueNames.reduce((result, valueName) => {
            return {
                ...result,
                [valueName]: [],
            };
        }, {}),
        tagMetrics: (tagNames || []).reduce((result, tagName) => {
            return {
                ...result,
                [tagName]: [],
            };
        }, {}),
    };
    // Convert collectionData.data, a double array of values, into a map of metricName => DataPoint[]
    const rows = collectionData.data;
    const resultData = rows.reduce((result, rowValues) => {
        // first element is the date of the data point
        const dataPointDate = rowValues[0];
        // Backend return values for the metrics in this order, value for valueNames and then value for tagNames
        [...valueNames, ...(tagNames || [])].forEach((name, index) => {
            // the value at index i + 1 of rowValues will be the value for metric name at index i
            const dataPointValue = rowValues[index + 1];
            if (valueNames.includes(name)) {
                // Add the dataPointValue to numericMetric[name]
                result.numericMetrics[name].push({
                    dateString: dataPointDate,
                    dateValue: Date.parse(dataPointDate),
                    value: dataPointValue,
                });
            }
            else {
                result.tagMetrics[name].push({
                    dateString: dataPointDate,
                    dateValue: Date.parse(dataPointDate),
                    value: dataPointValue,
                });
            }
        });
        return result;
    }, resultDataInit);
    return resultData;
};
/**
 * Convert data resolution in string format to a value in milliseconds. These are the different resolution that the backend will return
 * Res5Seconds  = "5sec"
 * Res15Seconds = "15sec"
 * Res1Minute   = "1min"
 * Res10Minutes = "10min"
 * Res1Hour     = "1hour"
 * Res1Day      = "1day"
 * Res1Week     = "1week"
 * Res1Month    = "1month"
 *
 * For example:
 *      '5sec' => 5000
 *      '15sec' => 15000
 *      '1min' => 60,000
 *      '10min' => 600,000
 * @param resolution
 */
export const resolutionToMilliseconds = (resolution) => {
    // first, break the resolution to amount and unit
    const amount = parseInt(resolution, 10);
    const unit = resolution.substring(amount.toString().length);
    // Get the number of milliseconds for the unit e.g. 'sec' => 1000, 'min' => 60000, 'hour' => 3600000 etc...
    const unitsInMilliseconds = ((u) => {
        if (u === 'year') {
            return Time.YEAR_IN_MS;
        }
        if (u === 'month') {
            return Time.MONTH_IN_MS;
        }
        if (u === 'week') {
            return Time.WEEK_IN_MS;
        }
        if (u === 'day') {
            return Time.DAY_IN_MS;
        }
        if (u === 'hour') {
            return Time.HOUR_IN_MS;
        }
        if (u === 'min') {
            return Time.MINUTE_IN_MS;
        }
        if (u === 'sec') {
            return Time.SECOND_IN_MS;
        }
        return 0;
    })(unit);
    return amount * unitsInMilliseconds;
};
// Sort NumericMetricInfo by displayName or valueName if displayName is not available
export const sortMetrics = (metrics) => {
    return metrics.sort((metric1, metric2) => {
        const name1 = (metric1.displayName || metric1.name).toLowerCase();
        const name2 = (metric2.displayName || metric2.name).toLowerCase();
        if (name1 < name2) {
            return -1;
        }
        if (name1 > name2) {
            return 1;
        }
        return 0;
    });
};
/**
 * Given a timeSpan, return the data resolution that TSDB back end uses
 *  "5sec"     15 minutes > timeSpan >= 5 minutes
 *  "15sec"    1 hour > timeSpan >= 15 minutes -- bucket size: 15 seconds
 *  "1min"     12 hours > timeSpan >= 1 hour -- bucket size: 1 minute
 *  "10min"    5 days > timeSpan >= 12 hours -- bucket size: 10 minutes
 *  "1hour"    28 days > timeSpan >= 5 days -- bucket size: 1 hour
 *  "1day"     1 year > timeSpan >= 28 days -- bucket size: 1 day
 *  "1week"    5 years > timeSpan >= 1 year -- bucket size: 1 week
 *  "1month"   timeSpan >= 5 years -- bucket size: 1 month
 */
export const getTimeSpanResolutionInMs = (timeSpan) => {
    if (timeSpan === TimeSpan.MINUTE) {
        return Time.SECOND_IN_MS * 5;
    }
    if (timeSpan === TimeSpan.HOUR) {
        return Time.SECOND_IN_MS * 15;
    }
    if (timeSpan === TimeSpan.DAY) {
        return Time.MINUTE_IN_MS * 10;
    }
    if (timeSpan === TimeSpan.WEEK) {
        return Time.HOUR_IN_MS;
    }
    if (timeSpan === TimeSpan.MONTH) {
        return Time.DAY_IN_MS;
    }
    if (timeSpan === TimeSpan.YEAR) {
        return Time.WEEK_IN_MS;
    }
    return Time.SECOND_IN_MS * 5;
};
